import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CURRENCY } from "../shared/currency";
import { getAccountGroupById, useQuery } from "wasp/client/operations";
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
} from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "../client/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../client/components/ui/card";
import { Progress } from "../client/components/ui/progress";
import { Badge } from "../client/components/ui/badge";
import { Separator } from "../client/components/ui/separator";
import useTranslation from "../client/hooks/useTranslation";
import AnimatedMetric from "../client/components/AnimatedMetric";
import { metricsContainerVariants, metricsItemVariants } from "../client/components/motionVariants";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(value: number, currency = "USD") {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function fmtPct(value: number) {
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <Card variant="accent">
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
        <p className="text-xl font-bold font-mono">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ─── Equity sparkline ─────────────────────────────────────────────────────────

function EquitySparkline({
  equityCurve,
  initialBalance,
}: {
  equityCurve: any[];
  initialBalance: number;
}) {
  if (!equityCurve?.length) return null;

  const balances = equityCurve.map((p) => p.balance);
  const min = Math.min(initialBalance, ...balances);
  const max = Math.max(initialBalance, ...balances);
  const range = max - min || 1;
  const w = 600;
  const h = 120;
  const pad = 8;

  const points = equityCurve.map((p, i) => {
    const x = pad + (i / Math.max(equityCurve.length - 1, 1)) * (w - pad * 2);
    const y = h - pad - ((p.balance - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  });

  const isPositive = balances[balances.length - 1] >= initialBalance;
  const strokeColor = isPositive
    ? "hsl(var(--edgely-pnl-positive))"
    : "hsl(var(--edgely-pnl-negative))";

  return (
    <div className="w-full overflow-hidden rounded-lg bg-muted/20 border border-border/30">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="w-full h-28"
        preserveAspectRatio="none"
      >
        <polyline
          fill="none"
          stroke={strokeColor}
          strokeWidth="2"
          points={points.join(" ")}
        />
      </svg>
    </div>
  );
}

// ─── Account mini-card inside group page ─────────────────────────────────────

function AccountMiniCard({
  account,
  t,
  onClick,
}: {
  account: any;
  t: ReturnType<typeof useTranslation>;
  onClick: () => void;
}) {
  const pnl = account.currentBalance - account.initialBalance;
  const variation = account.initialBalance > 0 ? (pnl / account.initialBalance) * 100 : 0;
  const pnlColor = pnl >= 0 ? "text-pnlPositive" : "text-destructive";

  const statusLabel =
    account.status === "challenge"
      ? t.tradingAccounts.statusChallenge
      : account.status === "pa"
      ? t.tradingAccounts.statusPa
      : account.status === "inactive"
      ? t.tradingAccounts.statusInactive
      : account.status === "demo"
      ? t.tradingAccounts.statusDemo
      : t.tradingAccounts.statusLive;

  const { challengeProgress, bufferStatus } = account;

  return (
    <Card
      className="cursor-pointer hover:border-primary/50 transition-colors"
      onClick={onClick}
    >
      <CardContent className="p-4 space-y-3">
        {/* Name + badges */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-medium truncate">{account.name}</p>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <Badge variant="outline" className="text-xs">{statusLabel}</Badge>
              {account.effectiveTemplate && (
                <Badge
                  className="text-xs"
                  style={{
                    backgroundColor: account.effectiveTemplate.color,
                    color: account.effectiveTemplate.textColor,
                  }}
                >
                  {account.effectiveTemplate.name}
                </Badge>
              )}
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="shrink-0 text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            <ExternalLink className="w-3 h-3" />
          </Button>
        </div>

        {/* Balance */}
        <div>
          <p className={`text-lg font-bold font-mono ${pnlColor}`}>
            {fmt(account.currentBalance, CURRENCY)}
          </p>
          <p className={`text-xs font-mono ${pnlColor}`}>
            {pnl >= 0 ? "+" : ""}{fmt(pnl, CURRENCY)} ({fmtPct(variation)})
          </p>
        </div>

        {/* Challenge progress bar */}
        {account.status === "challenge" && challengeProgress && (
          <div className="space-y-1">
            {challengeProgress.isComplete && (
              <div className="flex items-center gap-1 text-green-600 dark:text-green-400 text-xs">
                <CheckCircle className="w-3 h-3" />
                {t.tradingAccounts.challengeComplete}
              </div>
            )}
            {!challengeProgress.isComplete && (
              <>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{t.tradingAccounts.challengeProgress}</span>
                  <span className="font-mono">{fmtPct(challengeProgress.profitPercent)}</span>
                </div>
                <Progress value={challengeProgress.ratio * 100} className="h-1.5" />
              </>
            )}
          </div>
        )}

        {/* Buffer */}
        {bufferStatus && bufferStatus.ruleType !== null && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{t.tradingAccounts.bufferRemaining}</span>
              <span
                className={`font-mono ${bufferStatus.isViolation ? "text-destructive" : bufferStatus.isWarning ? "text-[hsl(var(--warning))]" : ""}`}
              >
                {fmt(bufferStatus.remainingRoom, CURRENCY)}
              </span>
            </div>
            <Progress
              value={bufferStatus.usedRatio * 100}
              className={`h-1.5 ${bufferStatus.isViolation ? "[&>div]:bg-destructive" : bufferStatus.isWarning ? "[&>div]:bg-[hsl(var(--warning))]" : ""}`}
            />
          </div>
        )}

        {/* Trades count */}
        <p className="text-xs text-muted-foreground">
          {account.tradesCount} {t.tradingAccounts.closedTrades}
        </p>
      </CardContent>
    </Card>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AccountGroupDetailPage() {
  const t = useTranslation();
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();

  const { data: group, isLoading } = useQuery(
    getAccountGroupById,
    { id: groupId ?? "" },
    { enabled: !!groupId },
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="text-muted-foreground mb-4">{t.tradingAccounts.loading}</p>
        <Button variant="outline" onClick={() => navigate("/trading-accounts")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t.tradingAccounts.backToAccounts}
        </Button>
      </div>
    );
  }

  const pnlColor = group.consolidatedPnl >= 0 ? "text-pnlPositive" : "text-destructive";
  const variation =
    group.totalInitialBalance > 0
      ? (group.consolidatedPnl / group.totalInitialBalance) * 100
      : 0;

  return (
    <div className="w-full py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/trading-accounts")}
          className="gap-2 text-muted-foreground hover:text-foreground -ml-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {t.tradingAccounts.backToAccounts}
        </Button>
      </div>

      {/* Hero */}
      <div className="space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold">{group.name}</h1>
          {group.propFirmTemplate && (
            <Badge
              className="text-xs font-medium"
              style={{
                backgroundColor: (group.propFirmTemplate as any).color,
                color: (group.propFirmTemplate as any).textColor,
              }}
            >
              {(group.propFirmTemplate as any).name}
            </Badge>
          )}
          <Badge variant="secondary" className="text-xs">
            {group.activeCount} / {group.totalCount} {t.tradingAccounts.accountsInGroup}
          </Badge>
        </div>
        <div className="flex items-baseline gap-3">
          <AnimatedMetric
            value={group.consolidatedPnl}
            format="currency"
            prefix={group.consolidatedPnl >= 0 ? "+" : undefined}
            suffix=" $"
            className={`text-3xl font-bold font-mono ${pnlColor}`}
          />
          <span className={`text-sm font-mono ${pnlColor}`}>({fmtPct(variation)})</span>
        </div>
        <p className="text-sm text-muted-foreground">
          {t.tradingAccounts.totalCurrentBalance}:{" "}
          <span className="font-mono">{fmt(group.totalCurrentBalance, CURRENCY)}</span>
          {" · "}
          {t.tradingAccounts.totalInitialBalance}:{" "}
          <span className="font-mono">{fmt(group.totalInitialBalance, CURRENCY)}</span>
        </p>
      </div>

      {/* Consolidated stats grid */}
      <motion.div
        key={`group-${groupId}`}
        variants={metricsContainerVariants}
        initial="hidden"
        animate="show"
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3"
      >
        <motion.div variants={metricsItemVariants}>
          <StatCard
            label={t.tradingAccounts.tradesCount}
            value={<AnimatedMetric value={group.totalTradesCount} format="integer" />}
          />
        </motion.div>
        <motion.div variants={metricsItemVariants}>
          <StatCard
            label={t.tradingAccounts.winRate}
            value={<AnimatedMetric value={group.winRate ?? 0} format="percent" suffix="%" />}
            sub={`${group.totalWins}W / ${group.totalLosses}L`}
          />
        </motion.div>
        <motion.div variants={metricsItemVariants}>
          <StatCard
            label={t.tradingAccounts.activeAccounts}
            value={<AnimatedMetric value={group.activeCount} format="integer" />}
            sub={`/ ${group.totalCount} ${t.tradingAccounts.totalAccounts}`}
          />
        </motion.div>
        {group.totalPayouts > 0 && (
          <motion.div variants={metricsItemVariants}>
            <StatCard
              label={t.tradingAccounts.totalPayoutsGroup}
              value={<AnimatedMetric value={group.totalPayouts} format="currency" suffix=" $" />}
            />
          </motion.div>
        )}
      </motion.div>

      {/* Main layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Equity curve */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t.tradingAccounts.equityCurve}</CardTitle>
            </CardHeader>
            <CardContent>
              {group.equityCurve?.length ? (
                <>
                  <EquitySparkline
                    equityCurve={group.equityCurve}
                    initialBalance={group.totalInitialBalance}
                  />
                  <div className="mt-2 flex justify-between text-xs text-muted-foreground font-mono">
                    <span>{(group.equityCurve as any[])[0]?.date}</span>
                    <span>{(group.equityCurve as any[])[(group.equityCurve as any[]).length - 1]?.date}</span>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  {t.tradingAccounts.noEquityData}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Accounts list (sidebar on large screens) */}
        <div>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t.tradingAccounts.groupAccounts}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {!(group.accounts as any[]).length && (
                <p className="text-sm text-muted-foreground">{t.tradingAccounts.noActiveAccounts}</p>
              )}
              {(group.accounts as any[]).map((account) => (
                <AccountMiniCard
                  key={account.id}
                  account={account}
                  t={t}
                  onClick={() => navigate(`/account/${account.id}`)}
                />
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
